import { forwardRef } from 'react';

export type NewTextAreaResize = 'none' | 'vertical' | 'horizontal' | 'both';

export interface NewTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  label?: string;
  /** Controls the CSS resize handle. Omit for the browser default (both). */
  resize?: NewTextAreaResize;
}

/**
 * NewTextArea — Bombiix master-design multi-line text input. Chrome mirrors
 * NewInput (4px radius, 1px #D8D8D8 border, Bombiix Blue focus border, red
 * invalid border + alert icon). Fully prop-driven height (`rows`) and resize
 * handle (`resize`) — no defaults applied.
 * Forwards refs so consumers can focus / measure.
 * Styling lives in `assets/scss/master-design/_textarea.scss` — no per-component CSS.
 */
const NewTextArea = forwardRef<HTMLTextAreaElement, NewTextAreaProps>(function NewTextArea(
  { className, invalid, label, resize, ...rest },
  ref,
) {
  const inputClass = [
    'bombiix-textarea',
    invalid ? 'bombiix-textarea--invalid' : '',
    resize ? `bombiix-textarea--resize-${resize}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="bombiix-textarea-wrap">
      {label ? <span className="bombiix-textarea__label">{label}</span> : null}
      <div className="bombiix-textarea__field">
        <textarea ref={ref} className={inputClass} {...rest} />
        {invalid ? (
          <span className="bombiix-textarea__alert-icon" aria-hidden="true">
            <i className="bombiix-icon-validation" />
          </span>
        ) : null}
      </div>
    </div>
  );
});

export default NewTextArea;
